import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as FileIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useDataProvider, useNotify } from 'react-admin';

interface FileUploadItem {
  file: File;
  category: string;
  description: string;
  isPublic: boolean;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  id?: string;
}

interface LocationFileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  onSuccess?: () => void;
}

const FILE_CATEGORIES = [
  'photos',
  'documents',
  'drawings',
  'permits',
  'contracts',
  'inspection_reports',
  'other'
];

export const LocationFileUploadDialog: React.FC<LocationFileUploadDialogProps> = ({
  open,
  onClose,
  locationId,
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Define allowed file extensions
    const allowedExtensions = [
      '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', // images
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.zip', '.rar' // documents
    ];

    // Filter files by extension
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    acceptedFiles.forEach(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (allowedExtensions.includes(ext)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(`${file.name} - unsupported file type`);
      }
    });

    if (rejectedFiles && rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(rejection => {
        const error = rejection.errors?.[0];
        if (error?.code === 'file-too-large') {
          return `${rejection.file.name} is too large. Max size is 10MB.`;
        }
        return `${rejection.file.name} was rejected.`;
      });
      notify(errors.join(', '), { type: 'error' });
    }

    if (invalidFiles.length > 0) {
      notify(invalidFiles.join(', '), { type: 'error' });
    }

    if (validFiles.length > 0) {
      const newFiles: FileUploadItem[] = validFiles.map(file => ({
        file,
        category: 'other',
        description: '',
        isPublic: false,
        status: 'pending',
        progress: 0,
      }));
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [notify]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: false,
    noKeyboard: false,
    preventDropOnDocument: true,
  });

  const updateFile = (index: number, updates: Partial<FileUploadItem>) => {
    setFiles(prev => prev.map((file, i) => i === index ? { ...file, ...updates } : file));
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (fileItem: FileUploadItem, index: number): Promise<boolean> => {
    try {
      updateFile(index, { status: 'uploading', progress: 0 });

      // First, upload the file to get a file ID
      const formData = new FormData();
      formData.append('file', fileItem.file);
      formData.append('folder', 'locations');
      formData.append('description', fileItem.description || fileItem.file.name);
      formData.append('isPublic', fileItem.isPublic.toString());

      // Simulate progress
      let currentProgress = 0;
      const progressInterval = setInterval(() => {
        currentProgress = Math.min(currentProgress + 10, 90);
        updateFile(index, { progress: currentProgress });
      }, 100);

      const uploadResponse = await dataProvider.create('files', {
        data: formData,
      });

      clearInterval(progressInterval);
      updateFile(index, { progress: 95 });

      const fileId = uploadResponse.data.id;

      // Then, link the file to the location
      const linkResponse = await dataProvider.create(`crm/locations/${locationId}/files`, {
        data: {
          fileId: fileId,
          category: fileItem.category,
          description: fileItem.description,
          isPublic: fileItem.isPublic,
        },
      });

      updateFile(index, {
        status: 'success',
        progress: 100,
        id: linkResponse.data.id,
      });

      return true;
    } catch (error: any) {
      updateFile(index, {
        status: 'error',
        progress: 0,
        error: error.message || 'Upload failed',
      });
      return false;
    }
  };

  const handleUploadAll = async () => {
    setUploading(true);
    const pendingFiles = files.filter(f => f.status === 'pending');

    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.status === 'pending') {
        const success = await uploadFile(file, i);
        if (success) successCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      notify(`Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}`, { type: 'success' });
      if (onSuccess) {
        onSuccess();
      }
    }

    // Close dialog if all files were uploaded successfully
    const allUploaded = files.every(f => f.status === 'success' || f.status === 'error');
    const anySuccess = files.some(f => f.status === 'success');
    if (allUploaded && anySuccess) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      onClose();
    }
  };

  const getStatusIcon = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'uploading':
        return <UploadIcon color="primary" />;
      default:
        return <FileIcon />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canUpload = files.length > 0 && files.some(f => f.status === 'pending') && !uploading;
  const hasErrors = files.some(f => f.status === 'error');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>Upload Files to Location</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {/* Dropzone */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to select files
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supports: Images, PDF, Word, Excel, Text, ZIP (Max 10MB per file)
            </Typography>
          </Box>

          {hasErrors && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Some files failed to upload. Please check the errors below and try again.
            </Alert>
          )}
        </Box>

        {/* Files List */}
        {files.length > 0 && (
          <List>
            {files.map((fileItem, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <ListItemIcon>
                  {getStatusIcon(fileItem.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {fileItem.file.name}
                      </Typography>
                      <Chip
                        label={formatFileSize(fileItem.file.size)}
                        size="small"
                        variant="outlined"
                      />
                      {fileItem.status === 'success' && (
                        <Chip label="Uploaded" size="small" color="success" />
                      )}
                      {fileItem.status === 'error' && (
                        <Chip label="Failed" size="small" color="error" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {fileItem.status === 'uploading' && (
                        <LinearProgress
                          variant="determinate"
                          value={fileItem.progress}
                          sx={{ mb: 1 }}
                        />
                      )}
                      {fileItem.status === 'error' && fileItem.error && (
                        <Typography variant="caption" color="error" display="block">
                          {fileItem.error}
                        </Typography>
                      )}
                      {fileItem.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={fileItem.category}
                              label="Category"
                              onChange={(e) => updateFile(index, { category: e.target.value })}
                            >
                              {FILE_CATEGORIES.map(cat => (
                                <MenuItem key={cat} value={cat}>
                                  {cat.replace('_', ' ').toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Description"
                            value={fileItem.description}
                            onChange={(e) => updateFile(index, { description: e.target.value })}
                            sx={{ flexGrow: 1 }}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={fileItem.isPublic}
                                onChange={(e) => updateFile(index, { isPublic: e.target.checked })}
                                size="small"
                              />
                            }
                            label="Public"
                          />
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {fileItem.status === 'pending' && (
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {files.some(f => f.status === 'success') ? 'Close' : 'Cancel'}
        </Button>
        {canUpload && (
          <Button
            variant="contained"
            onClick={handleUploadAll}
            disabled={uploading}
            startIcon={<UploadIcon />}
          >
            Upload {files.filter(f => f.status === 'pending').length} File
            {files.filter(f => f.status === 'pending').length !== 1 ? 's' : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};